import React, { useCallback } from 'react';

import { StyleSheet } from 'react-native';

import { LayoutContainer } from 'components/layout/styled';
import { ResponsiveCarousel } from 'components/responsive-carousel';
import { Box, Typography } from 'components/ui';
import { ILocaleImageFragment, ILocaleStringFragment } from 'generated/sanity-graphql';
import { t } from 'state/intl/translations';
import { getDesignToken, primitive, spacing } from 'styles/constants';

import { Image } from './styled';

export type IReward = {
  image: ILocaleImageFragment;
  name: ILocaleStringFragment;
};

export type RewardCarouselCategory = {
  label: ILocaleStringFragment;
  rewards: Array<IReward>;
};

export type IRewardsCarouselProps = {
  rewardsCarouselTitle: {
    locale: string;
  };
  rewardsCarouselDescription: {
    locale: string;
  };
  rewardCarouselCategories: Array<RewardCarouselCategory>;
};

const styles: { background: string; color: string }[] = [
  {
    background: 'transparent',
    color: primitive.$bbqBrown,
  },
  {
    background: '#fe8731',
    color: primitive.$bbqBrown,
  },
  {
    background: getDesignToken('color-tertiary'),
    color: primitive.$mayoEggWhite,
  },
  {
    background: primitive.$fieryRed,
    color: primitive.$mayoEggWhite,
  },
  {
    background: primitive.$meltyYellow,
    color: primitive.$bbqBrown,
  },
];

const itemNameLocaleKeyExtractor = ({ name }: IReward, index: number) =>
  name.locale || index.toString();

const RewardsCarouselWidget: React.FC<React.PropsWithChildren<IRewardsCarouselProps>> = ({
  rewardsCarouselTitle,
  rewardsCarouselDescription,
  rewardCarouselCategories,
}) => {
  const RewardsCategories = ({
    rewardCategory,
    categoryIndex,
  }: {
    rewardCategory: RewardCarouselCategory;
    categoryIndex: number;
  }) => (
    <Box style={componentStyles.rewardCategory}>
      <Typography variant="headerOne" mb="$0">
        {rewardCategory.label.locale} {t('crowns')}
      </Typography>
      <ResponsiveCarousel
        alignWithParent
        data={rewardCategory.rewards}
        itemWidth={260}
        centerCards
        keyExtractor={itemNameLocaleKeyExtractor}
        renderItem={useCallback(
          ({ item }: { item: IReward }) => {
            const styleIndex = categoryIndex > 5 ? 0 : categoryIndex;
            const itemNameColor = styles[styleIndex].color ?? 'black';
            const carouselBackgroundColor = styles[styleIndex].background ?? 'transparent';

            return (
              <Box
                style={[componentStyles.rewardCard, { backgroundColor: carouselBackgroundColor }]}
              >
                <Image
                  lockedAspectRatio
                  objectFitContain
                  alt={item.name.locale ?? ''}
                  image={item.image.locale}
                />
                <Typography color={itemNameColor}>{item.name.locale}</Typography>
              </Box>
            );
          },
          [categoryIndex]
        )}
      />
    </Box>
  );

  return (
    <LayoutContainer>
      <Typography variant="headerOne">{rewardsCarouselTitle.locale}</Typography>
      <Typography>{rewardsCarouselDescription.locale}</Typography>
      {rewardCarouselCategories.map((rewardCategory, i) => (
        <RewardsCategories key={i} categoryIndex={i} rewardCategory={rewardCategory} />
      ))}
    </LayoutContainer>
  );
};

export default RewardsCarouselWidget;

const componentStyles = StyleSheet.create({
  rewardCategory: {
    alignItems: 'center',
    margin: 0,
    marginTop: spacing.$10,
  },
  rewardCard: {
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    paddingHorizontal: spacing.$2,
    paddingVertical: spacing.$2,
  },
});
